import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiChevronDown, FiFilter, FiGrid, FiList, FiX } from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ProductCard, Product } from '../components/ProductCard';
import { getActiveProducts, FrontendProduct } from '../api/productApi';
import PriceRangeSlider from '../components/PriceRangeSlider';
import CategoryFilter, { categorySizeTypes } from '../components/CategoryFilter';

interface CategoryPageProps {}

// Extended product interface for additional display properties
interface ExtendedProduct extends FrontendProduct {
  category?: string;
}

const CategoryPage: React.FC<CategoryPageProps> = () => {
  const { id } = useParams<{ id: string }>();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visibleProductCount, setVisibleProductCount] = useState(12);
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedBrand, setSelectedBrand] = useState("All");
  const [selectedSize, setSelectedSize] = useState("All");
  const [minPrice, setMinPrice] = useState<number>(0);
  const [maxPrice, setMaxPrice] = useState<number>(15000);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [categoryTitle, setCategoryTitle] = useState("");
  const [categoryDescription, setCategoryDescription] = useState("");
  
  // Force scroll to top on component mount
  useEffect(() => {
    // Immediately scroll to top with no animation to ensure the page starts at the top
    window.scrollTo(0, 0);
  }, []);
  
  // Generate brand options dynamically from available products
  const generateBrandOptions = () => {
    // Always include "All Brands" option
    const options = [{ id: 'All', label: 'All Brands' }];
    
    // Get unique brands from products
    const uniqueBrands = Array.from(new Set(products.map(product => product.brand)));
    
    // Sort brands alphabetically
    uniqueBrands.sort();
    
    // Add each unique brand to options
    uniqueBrands.forEach(brand => {
      if (brand && brand.trim() !== '') {
        options.push({ id: brand, label: brand });
      }
    });
    
    return options;
  };
  
  // Generate brand options based on available products
  const brands = generateBrandOptions();
  
  // Filter options - kept static as these are logical groupings
  const filters = [
    { id: 'all', label: 'All' },
    { id: 'grails', label: 'Grails' },
    { id: 'new', label: 'New Arrivals' }
  ];
  
  // Get dynamic size options based on available products in this category
  const getDynamicSizeOptions = () => {
    const options = ['All']; // Always include "All" option
    
    // Get all available sizes from products that match this category
    const allSizes: string[] = [];
    products.forEach(product => {
      product.sizes.forEach(size => {
        if (!allSizes.includes(size)) {
          allSizes.push(size);
        }
      });
    });
    
    // Sort sizes numerically if possible
    allSizes.sort((a, b) => {
      // If sizes contain letters, use a more complex sorting
      if (a.match(/[a-zA-Z]/) || b.match(/[a-zA-Z]/)) {
        return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
      }
      // Otherwise, sort numerically
      return parseFloat(a) - parseFloat(b);
    });
    
    return options.concat(allSizes);
  };
  
  // Generate dynamic price ranges based on available products
  const generatePriceRanges = () => {
    // Always include "All Prices" option
    const options = [{ id: 'All', label: 'All Prices' }];
    
    // Standard price ranges
    const standardRanges = [
      { id: '0-100', label: 'Under $100' },
      { id: '100-200', label: '$100 - $200' },
      { id: '200-300', label: '$200 - $300' },
      { id: '300-500', label: '$300 - $500' },
      { id: '500+', label: 'Over $500' }
    ];
    
    // Only include price ranges that have products
    standardRanges.forEach(range => {
      const [min, max] = range.id.split('-').map(Number);
      const hasProductsInRange = products.some(product => {
        if (!max) {
          // Handle "500+" case
          return product.price >= min;
        }
        return product.price >= min && product.price <= max;
      });
      
      if (hasProductsInRange) {
        options.push(range);
      }
    });
    
    return options;
  };
  
  // Generate price range options based on available products
  const priceRanges = generatePriceRanges();
  
  // Category display names
  const categoryDisplayNames: { [key: string]: string } = {
    "mens": "Men's Sneakers",
    "womens": "Women's Sneakers",
    "toddlers": "Toddlers (TD) Sneakers",
    "grade-school": "Grade School (GS) Sneakers"
  };

  // Fetch products based on category
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        
        // Fetch real products using the API
        const data = await getActiveProducts();
        
        // Filter products by category based on size type
        const categoryTypeCode = categorySizeTypes[id || 'mens'] || 'M';
        let filteredProducts: Product[] = [];
        
        if (data && data.length > 0) {
          // Filter and transform the products
          filteredProducts = data
            .filter(product => {
              // Match by size type for the category
              if (!product.sizeType) return false;
              
              if (id === 'mens') {
                return product.sizeType.includes('M') && !product.sizeType.includes('W');
              } else if (id === 'womens') {
                return product.sizeType.includes('W');
              } else if (id === 'grade-school') {
                return product.sizeType.includes('GS') || product.sizeType.includes('Y');
              } else if (id === 'toddlers') {
                return product.sizeType.includes('TD') || product.sizeType.includes('C');
              }
              
              return true;
            })
            .map(product => {
              // Create a sizeDisplay if it's missing
              let sizeDisplay = product.sizeDisplay;
              if (!sizeDisplay && product.size) {
                const sizeType = product.sizeType || '';
                if (sizeType === 'M' || sizeType === 'W') {
                  sizeDisplay = `${product.size}${sizeType}`;
                } else if (sizeType === 'TD' || sizeType === 'GS' || sizeType.includes('Y') || sizeType.includes('C')) {
                  sizeDisplay = `${product.size} (${sizeType})`;
                } else {
                  // If sizeType is unknown but we can infer from category
                  const category = id || 'mens';
                  const categoryCode = categorySizeTypes[category] || '';
                  
                  if (categoryCode === 'M' || categoryCode === 'W') {
                    sizeDisplay = `${product.size}${categoryCode}`;
                  } else if (categoryCode) {
                    sizeDisplay = `${product.size} (${categoryCode})`;
                  } else {
                    // Last resort - just use the size as is
                    sizeDisplay = product.size;
                  }
                }
              }
              
              return {
                id: product.id,
                name: product.name,
                brand: product.brand,
                price: product.price,
                image: product.image,
                sizes: product.sizes || [],
                condition: product.condition,
                collab: product.collab,
                category: id || 'mens',
                sizeDisplay: sizeDisplay,
                sizeType: product.sizeType
              };
            });
        }
        
        setProducts(filteredProducts);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load products for this category.');
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, [id]);
  
  // Get current applicable sizes based on category
  const getCurrentSizes = () => {
    return getDynamicSizeOptions();
  };
  
  // Apply filters to products
  const filteredProducts = products.filter(product => {
    // Filter by brand
    if (selectedBrand !== 'All' && product.brand !== selectedBrand) {
      return false;
    }
    
    // Filter by specific size
    if (selectedSize !== 'All') {
      if (!product.sizes.includes(selectedSize)) {
        return false;
      }
    }
    
    // Filter by price range
    if (minPrice > 0 || maxPrice < 15000) {
      if (product.price < minPrice || product.price > maxPrice) return false;
    }
    
    // Filter by type
    if (activeFilter === 'grails') {
      // Assuming grails are products with collab or price >= 500
      return (product.collab && product.collab.trim() !== '') || product.price >= 500;
    } else if (activeFilter === 'new') {
      // Placeholder logic for new arrivals
      // In a real app, you would use createdAt date or a specific flag
      return true;
    }
    
    return true;
  });
  
  const handleLoadMore = () => {
    setVisibleProductCount(prev => prev + 8);
  };
  
  // Get the display name for the category
  const getCategoryDisplayName = () => {
    return categoryDisplayNames[id || 'mens'] || 'All Products';
  };
  
  const handleFilterChange = (filterId: string) => {
    setActiveFilter(filterId);
  };
  
  const handleBrandChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedBrand(e.target.value);
  };
  
  const handleSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedSize(e.target.value);
  };
  
  const handlePriceChange = (min: number, max: number) => {
    setMinPrice(min);
    setMaxPrice(max);
  };

  // Refresh filter options whenever products change
  useEffect(() => {
    // Only update if we have products and we're not in a loading state
    if (products.length > 0 && !loading) {
      // Get the available brands based on current products
      generateBrandOptions();
      
      // Get available price ranges based on current products
      generatePriceRanges();
    }
  }, [products, loading]);

  return (
    <div className="min-h-screen bg-background-dark text-primary">
      <Header />
      
      <main className="py-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page header with breadcrumbs */}
        <div className="mb-8">
          <div className="flex items-center space-x-2 text-sm text-secondary mb-4">
            <Link to="/" className="hover:text-accent transition-colors">Home</Link>
            <span>/</span>
            <span className="text-primary">{getCategoryDisplayName()}</span>
          </div>
          
          <h1 className="text-2xl sm:text-3xl font-bold">{getCategoryDisplayName()}</h1>
          <p className="text-secondary mt-2">Shop our collection of {getCategoryDisplayName().toLowerCase()}</p>
        </div>
        
        {/* Mobile filter button - only visible on small screens */}
        <div className="sm:hidden mb-6">
          <button 
            onClick={() => setMobileFiltersOpen(true)}
            className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-background-light rounded-md hover:bg-background-light/80 transition-colors"
          >
            <FiFilter className="w-4 h-4" />
            <span>Filters</span>
          </button>
        </div>
        
        {/* Filter and product section */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filter section - desktop */}
          <aside className="hidden sm:block lg:col-span-1 space-y-6">
            <div className="bg-background p-6 rounded-lg">
              <h2 className="text-lg font-medium mb-4">Filters</h2>
              
              {/* Brand filter */}
              <div className="mb-6">
                <label htmlFor="brand" className="block text-sm font-medium mb-2">Brand</label>
                <select
                  id="brand"
                  value={selectedBrand}
                  onChange={handleBrandChange}
                  className="w-full bg-background-light border border-gray-700 rounded-md py-2 px-3 text-primary focus:border-accent focus:ring-1 focus:ring-accent"
                >
                  {brands.map(brand => (
                    <option key={brand.id} value={brand.id}>{brand.label}</option>
                  ))}
                </select>
              </div>
              
              {/* Size filter */}
              <div className="mb-6">
                <label htmlFor="size" className="block text-sm font-medium mb-2">Size</label>
                <select
                  id="size"
                  value={selectedSize}
                  onChange={handleSizeChange}
                  className="w-full bg-background-light border border-gray-700 rounded-md py-2 px-3 text-primary focus:border-accent focus:ring-1 focus:ring-accent"
                >
                  {getCurrentSizes().map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
              </div>
              
              {/* Price range filter */}
              <div className="mb-6">
                <label className="block text-sm font-medium mb-2">Price</label>
                <PriceRangeSlider
                  minValue={0}
                  maxValue={15000}
                  step={50}
                  onChange={handlePriceChange}
                  defaultMinPrice={minPrice}
                  defaultMaxPrice={maxPrice}
                />
              </div>
            </div>
          </aside>
          
          {/* Mobile filter drawer - Add this to match AllGrailsPage */}
          {mobileFiltersOpen && (
            <div className="fixed inset-0 flex z-40 sm:hidden" aria-modal="true">
              {/* Backdrop */}
              <div 
                className="fixed inset-0 bg-black/70 transition-opacity"
                aria-hidden="true"
                onClick={() => setMobileFiltersOpen(false)}
              ></div>
              
              {/* Sliding panel */}
              <div className="relative ml-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-background px-4 py-6 shadow-xl">
                {/* Close button */}
                <div className="flex items-center justify-between mb-6 px-2">
                  <h2 className="text-lg font-medium">Filters</h2>
                  <button
                    type="button"
                    className="p-2 text-secondary hover:text-primary"
                    onClick={() => setMobileFiltersOpen(false)}
                  >
                    <span className="sr-only">Close menu</span>
                    <FiX className="h-6 w-6" />
                  </button>
                </div>
                
                {/* Filter content - mobile */}
                <div className="space-y-6">
                  {/* Brand filter */}
                  <div>
                    <label htmlFor="mobile-brand" className="block text-sm font-medium mb-2">Brand</label>
                    <select
                      id="mobile-brand"
                      value={selectedBrand}
                      onChange={handleBrandChange}
                      className="w-full bg-background-light border border-gray-700 rounded-md py-3 px-3 text-primary focus:border-accent focus:ring-1 focus:ring-accent"
                    >
                      {brands.map(brand => (
                        <option key={brand.id} value={brand.id}>{brand.label}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Size filter */}
                  <div>
                    <label htmlFor="mobile-size" className="block text-sm font-medium mb-2">Size</label>
                    <select
                      id="mobile-size"
                      value={selectedSize}
                      onChange={handleSizeChange}
                      className="w-full bg-background-light border border-gray-700 rounded-md py-3 px-3 text-primary focus:border-accent focus:ring-1 focus:ring-accent"
                    >
                      {getCurrentSizes().map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Price range filter */}
                  <div>
                    <label className="block text-sm font-medium mb-2">Price</label>
                    <PriceRangeSlider
                      minValue={0}
                      maxValue={15000}
                      step={50}
                      onChange={handlePriceChange}
                      defaultMinPrice={minPrice}
                      defaultMaxPrice={maxPrice}
                    />
                  </div>
                  
                  {/* Apply filters button */}
                  <div className="mt-8">
                    <button
                      className="w-full bg-accent text-white py-3 px-4 rounded-md hover:bg-accent/90 transition-colors"
                      onClick={() => setMobileFiltersOpen(false)}
                    >
                      Apply Filters
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Products grid */}
          <div className="lg:col-span-3">
            {/* Filter tabs */}
            <div className="mb-6 flex flex-wrap gap-2 items-center overflow-x-auto pb-1">
              {filters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => handleFilterChange(filter.id)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                    activeFilter === filter.id 
                      ? 'bg-accent text-white' 
                      : 'bg-background-light text-secondary hover:text-primary'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
              
              {/* Applied filters summary */}
              {(selectedBrand !== 'All' || selectedSize !== 'All' || (minPrice > 0 || maxPrice < 15000)) && (
                <div className="text-sm text-secondary ml-auto">
                  <span className="hidden sm:inline">Filters: </span>
                  {selectedBrand !== 'All' && <span className="ml-1">{selectedBrand}</span>}
                  {selectedSize !== 'All' && <span className="ml-1">Size {selectedSize}</span>}
                  {minPrice > 0 && <span className="ml-1">${minPrice} - ${maxPrice}</span>}
                </div>
              )}
            </div>
            
            {/* Loading state */}
            {loading && (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent mb-4"></div>
                <p className="text-secondary">Loading products...</p>
              </div>
            )}
            
            {/* Error state */}
            {error && (
              <div className="bg-red-900/20 rounded-lg p-8 text-center my-8">
                <div className="text-red-400 mb-3">{error}</div>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-background-light hover:bg-accent text-white rounded-md transition-colors"
                >
                  Retry
                </button>
              </div>
            )}
            
            {/* No results */}
            {!loading && !error && filteredProducts.length === 0 && (
              <div className="bg-background-light/20 rounded-lg p-8 text-center my-8">
                <div className="text-secondary mb-3">No products found matching your filters.</div>
                <button
                  onClick={() => {
                    setSelectedBrand('All');
                    setSelectedSize('All');
                    setMinPrice(0);
                    setMaxPrice(15000);
                    setActiveFilter('all');
                  }}
                  className="px-4 py-2 bg-accent hover:bg-accent/90 text-white rounded-md transition-colors"
                >
                  Clear All Filters
                </button>
              </div>
            )}
            
            {/* Products grid */}
            {!loading && !error && filteredProducts.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 sm:gap-6">
                {filteredProducts.slice(0, visibleProductCount).map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
            
            {/* Load more button */}
            {!loading && !error && filteredProducts.length > visibleProductCount && (
              <div className="flex justify-center mt-12">
                <button
                  onClick={handleLoadMore}
                  className="px-6 py-3 bg-background-light hover:bg-background text-primary rounded-md transition-colors"
                >
                  Load More
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CategoryPage;